import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";

import { strings } from "@/constants";
import { ButtonTypeEnum, SeniorityEnum } from "@/enums";

const optionsList: { label: string, value: string }[] = [
    {
        label: strings.benchmark.seniorityJunior,
        value: SeniorityEnum.JUNIOR,
    },
    {
        label: strings.benchmark.seniorityMid,
        value: SeniorityEnum.MID,
    },
    {
        label: strings.benchmark.senioritySenior,
        value: SeniorityEnum.SENIOR,
    },
    {
        label: strings.benchmark.seniorityLead,
        value: SeniorityEnum.LEAD,
    },
    {
        label: strings.benchmark.seniorityPrincipal,
        value: SeniorityEnum.PRINCIPAL,
    },
];

const tierMarkers = ["I", "II", "III", "IV", "V"];

interface MenuPositionInterface {
    left: number,
    top: number,
    width: number,
}

export const SenioritySelect = ({
    fieldClassName,
    id,
    isDisabled = false,
    isError = false,
    onChange,
    selectedValue,
}: {
    fieldClassName: string,
    id: string,
    isDisabled?: boolean,
    isError?: boolean,
    onChange: (value: string) => void,
    selectedValue: string,
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const menuRef = useRef<HTMLUListElement>(null);

    const [isOpen, setIsOpen] = useState(false);

    const [position, setPosition] = useState<MenuPositionInterface>({
        left: 0,
        top: 0,
        width: 0,
    });

    const selectedOption = optionsList.find(({ value }) => value === selectedValue);

    const selectedIndex = optionsList.findIndex(({ value }) => value === selectedValue);

    const toggleOpenHandler = () => {
        if (!isDisabled) setIsOpen((prev) => !prev);
    };

    const selectOptionHandler = (next: string) => {
        onChange(next);

        setIsOpen(false);
    };

    useLayoutEffect(
        () => {
            if (!isOpen || !triggerRef.current) return;

            const updatePositionHandler = () => {
                if (!triggerRef.current) return;

                const rect = triggerRef.current.getBoundingClientRect();

                setPosition({
                    left: rect.left,
                    top: rect.bottom + 8,
                    width: rect.width,
                });
            };

            updatePositionHandler();

            window.addEventListener(
                "resize",
                updatePositionHandler,
            );

            window.addEventListener(
                "scroll",
                updatePositionHandler,
                true,
            );

            return () => {
                window.removeEventListener(
                    "resize",
                    updatePositionHandler,
                );

                window.removeEventListener(
                    "scroll",
                    updatePositionHandler,
                    true,
                );
            };
        },
        [isOpen],
    );

    useEffect(
        () => {
            if (!isOpen) return;

            const clickOutsideHandler = (event: MouseEvent) => {
                const { target } = event;

                const isInsideTrigger = triggerRef.current?.contains(target as Node);

                const isInsideMenu = menuRef.current?.contains(target as Node);

                if (!isInsideTrigger && !isInsideMenu) setIsOpen(false);
            };

            const escKeyHandler = (event: KeyboardEvent) => {
                if (event.key === "Escape") setIsOpen(false);
            };

            document.addEventListener(
                "mousedown",
                clickOutsideHandler,
            );

            document.addEventListener(
                "keydown",
                escKeyHandler,
            );

            return () => {
                document.removeEventListener(
                    "mousedown",
                    clickOutsideHandler,
                );

                document.removeEventListener(
                    "keydown",
                    escKeyHandler,
                );
            };
        },
        [isOpen],
    );

    return (
        <>
            <button
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                disabled={isDisabled}
                id={id}
                ref={triggerRef}
                type={ButtonTypeEnum.BUTTON}
                className={`
                    ${fieldClassName}
                    group
                    flex
                    cursor-pointer
                    items-center
                    justify-between
                    text-left
                `}
                onClick={toggleOpenHandler}
            >
                <span
                    className={`
                        flex
                        items-center
                        gap-3
                        ${selectedOption ? "" : "text-text-muted/60"}
                    `}
                >
                    {selectedOption && (
                        <span
                            className="
                                inline-flex
                                h-6
                                w-7
                                items-center
                                justify-center
                                rounded-md
                                font-mono
                                text-[10px]
                                font-semibold
                                tracking-[0.18em]
                                text-primary
                            "
                            style={{ backgroundColor: "rgba(19, 86, 188, 0.10)" }}
                        >
                            {tierMarkers[selectedIndex]}
                        </span>
                    )}
                    <span>
                        {selectedOption ? selectedOption.label : strings.benchmark.seniorityPlaceholder}
                    </span>
                </span>
                <span
                    className={`
                        ml-3
                        inline-flex
                        h-7
                        w-7
                        flex-shrink-0
                        items-center
                        justify-center
                        rounded-full
                        text-primary
                        transition-all
                        duration-300
                        ${isOpen ? "rotate-180" : ""}
                    `}
                    style={{ backgroundColor: "rgba(19, 86, 188, 0.08)" }}
                    aria-hidden
                >
                    <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M19 9l-7 7-7-7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </button>
            {isOpen && createPortal(
                <ul
                    aria-label={strings.benchmark.seniorityRoleAriaLabel}
                    ref={menuRef}
                    role="listbox"
                    className="dropdown-rise overflow-hidden rounded-2xl"
                    style={{
                        backgroundColor: "#FFFFFF",
                        border: isError ? "1px solid rgba(220, 38, 38, 0.30)" : "1px solid rgba(19, 86, 188, 0.18)",
                        boxShadow: "0 28px 70px -22px rgba(0, 37, 186, 0.40), 0 8px 22px -10px rgba(0, 37, 186, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.95)",
                        left: `${position.left}px`,
                        position: "fixed",
                        top: `${position.top}px`,
                        width: `${position.width}px`,
                        zIndex: 1000,
                    }}
                >
                    <div
                        className="
                            flex
                            items-center
                            justify-between
                            px-5
                            pt-3
                            pb-2
                        "
                    >
                        <span
                            className="
                                font-mono
                                text-[10px]
                                font-semibold
                                tracking-[0.32em]
                                text-primary/55
                                uppercase
                            "
                        >
                            {strings.benchmark.seniorityScreenReaderLabel}
                        </span>
                        <span
                            className="
                                font-mono
                                text-[10px]
                                tracking-[0.18em]
                                text-text-muted
                                uppercase
                            "
                        >
                            {`${optionsList.length} tiers`}
                        </span>
                    </div>
                    <div
                        className="mx-5 h-px"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(19, 86, 188, 0.20), transparent)" }}
                    />
                    <div className="py-1.5">
                        {optionsList.map((option, index) => {
                            const {
                                label,
                                value,
                            } = option;

                            const isSelected = value === selectedValue;

                            return (
                                <li
                                    aria-selected={isSelected}
                                    key={value}
                                    role="option"
                                    style={{ animationDelay: `${40 + index * 32}ms` }}
                                    className="dropdown-item-rise"
                                >
                                    <button
                                        type={ButtonTypeEnum.BUTTON}
                                        className={`
                                            relative
                                            flex
                                            w-full
                                            cursor-pointer
                                            items-center
                                            gap-3
                                            px-5
                                            py-2.5
                                            text-left
                                            text-[15px]
                                            transition-all
                                            duration-200
                                            ${isSelected ? "text-primary" : "text-text-primary/85"}
                                        `}
                                        style={{ backgroundColor: isSelected ? "rgba(19, 86, 188, 0.09)" : "transparent" }}
                                        onClick={() => selectOptionHandler(value)}
                                        onMouseEnter={(event) => {
                                            if (!isSelected) event.currentTarget.style.backgroundColor = "rgba(19, 86, 188, 0.045)";
                                        }}
                                        onMouseLeave={(event) => {
                                            if (!isSelected) event.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <span
                                            className={`
                                                inline-flex
                                                h-6
                                                w-7
                                                flex-shrink-0
                                                items-center
                                                justify-center
                                                rounded-md
                                                font-mono
                                                text-[10px]
                                                font-semibold
                                                tracking-[0.18em]
                                                ${isSelected ? "text-primary" : "text-primary/55"}
                                            `}
                                            style={{ backgroundColor: isSelected ? "rgba(19, 86, 188, 0.16)" : "rgba(19, 86, 188, 0.06)" }}
                                        >
                                            {tierMarkers[index]}
                                        </span>
                                        <span
                                            className={`
                                                flex-1
                                                tracking-[0.02em]
                                                ${isSelected ? "font-semibold" : "font-medium"}
                                            `}
                                        >
                                            {label}
                                        </span>
                                        {isSelected && (
                                            <span
                                                className="
                                                    inline-flex
                                                    h-5
                                                    w-5
                                                    flex-shrink-0
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    text-white
                                                "
                                                style={{ backgroundColor: "#1356BC" }}
                                                aria-hidden
                                            >
                                                <svg
                                                    className="h-3 w-3"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={3}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        d="M5 13l4 4L19 7"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </div>
                </ul>,
                document.body,
            )}
        </>
    );
};
